import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ContextStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { UserReviewViewModel } from 'models/hotel';

import { UitkIcon, UitkIconSize } from 'uitk-react-icons';

import { LocalizedToggle } from 'components/common/localized-toggle';
import { MAX_RATING } from 'components/infosite/property-details/bar-ratings';
import { ReviewByline, ReviewManagerDetails, ReviewManagerResponse, ReviewText } from 'components/common/reviews/review-components';
import { ReviewGallery } from 'components/common/reviews/review-gallery';
import { ReviewHelpfulVote } from 'components/infosite/property-details/review-helpful-vote';
import { ReviewTranslate } from './review-translate';

export type ReviewLocalizedProps = ReviewProps & LocalizedComponentProps;

export interface ReviewProps {
  review: UserReviewViewModel;
  hideManagerResponses?: boolean;
  hideTopPadding?: boolean;
  searchText?: string;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  translatedReviewText?: string;
  translatedReviewTitle?: string;
  reviewTranslationState?: string;
}

export interface ReviewState {
  initialized: boolean;
  userReviewText: string;
  translatedReviewText: string;
  translatedReviewTitle: string;
  reviewTranslationState: string;
}

export interface TranslatedReview {
  translatedReviewText: string;
  translatedReviewTitle: string;
}

@withLocalization
@inject('currentHotel', 'context')
export class Review extends React.Component<ReviewLocalizedProps, ReviewState> {
  private siteLocale: string;
  constructor(props) {
    super(props);
    this.state = {
      initialized: false,
      userReviewText: this.props.review.reviewText,
      translatedReviewText: this.props.translatedReviewText || '',
      translatedReviewTitle: this.props.translatedReviewTitle || '',
      reviewTranslationState: this.props.reviewTranslationState || 'original'
    };
    this.translateReview = this.translateReview.bind(this);
    this.fetchTranslatedReview = this.fetchTranslatedReview.bind(this);
    this.siteLocale = this.props.context.locale;
  }

  public componentDidMount() {
    this.setState({ initialized: true });
  }

  public async translateReview() {
    const { reviewTranslationState, translatedReviewText } = this.state;

    if (reviewTranslationState === 'translated') {
      this.setState({ reviewTranslationState: 'original' });
    } else {
      if (translatedReviewText && translatedReviewText.length) {
        this.setState({ reviewTranslationState: 'translated' });
      } else {
        this.setState({ reviewTranslationState: 'loading' });
        const translatedReview = await this.fetchTranslatedReview(this.props.review.reviewId);

        this.setState({
          reviewTranslationState: 'translated',
          translatedReviewText: translatedReview['translatedReviewText'],
          translatedReviewTitle: translatedReview['translatedReviewTitle']
        });
      }
    }
  }

  public async fetchTranslatedReview(reviewId: string): Promise<TranslatedReview> {
    const { context, currentHotel } = this.props;
    const translatedReview: TranslatedReview = {
      translatedReviewText: '',
      translatedReviewTitle: ''
    };

    const fetchReview = currentHotel.fetchTranslatedReview(context, currentHotel.hotelId, reviewId, context.locale);
    const { review } = await fetchReview;
    if (review) {
      translatedReview.translatedReviewText = review.text;
      translatedReview.translatedReviewTitle = review.title;
    }

    return translatedReview;
  }

  public render() {
    const { l10n, review, hideManagerResponses, hideTopPadding, searchText } = this.props;
    const {
      managementResponses,
      photos,
      ratingOverall,
      reviewId,
      reviewSubmissionTime,
      stayDuration,
      superlative,
      title,
      travelers,
      themes
    } = review;
    const { initialized, reviewTranslationState, translatedReviewTitle, translatedReviewText } = this.state;
    const reviewTextProps = {
      review,
      l10n,
      searchText,
      reviewTranslationState,
      translatedText: translatedReviewText
    };

    const padding = hideTopPadding ? 'all-b-padding-six' : 'all-y-padding-six';

    return (
      <article key={`review-card-${reviewId}`} id={reviewId} className={`review-card ${padding}`}>
        <section className="review-meta">
          {ratingOverall &&
            ratingOverall > 0 && (
              <h3 className="uitk-type-heading-500 all-b-padding-three">
                {ratingOverall}/{MAX_RATING}
                {ratingOverall > 3 && (
                  <span data-stid="review-superlative">
                    <React.Fragment> {superlative}</React.Fragment>
                  </span>
                )}
              </h3>
            )}
        </section>

        <section className="review-byline all-b-padding-two">
          <ReviewByline l10n={l10n} {...review} />
          {travelers && travelers.length > 0 && <div className="uitk-type-300 pwa-theme--grey-700">{travelers.join(', ')}</div>}
          <span className="review-meta-date uitk-type-300 pwa-theme--grey-700">
            {reviewSubmissionTime ? l10n.formatDateString(reviewSubmissionTime, { skeleton: 'yMMMd' }) : ''}
          </span>
        </section>
        {!review.positiveRemarks &&
          !review.negativeRemarks &&
          themes && (
            <AbacusExperiment name="PWA_UGC_Themes">
              <AbacusControl />
              <AbacusVariant bucket={1}>
                <div className="uitk-grid pwa-theme--grey-700">
                  <ul className="uitk-type-200 no-bullet">
                    {themes.map(
                      theme =>
                        theme && theme.icon.id && theme.label ? (
                          <li className="all-b-padding-two" data-stid="themes-tag" key={`${reviewId}-${theme.icon.id}`}>
                            <UitkIcon name={theme.icon.id} size={UitkIconSize.SMALL} className="all-r-padding-one" />
                            <span>{theme.label}</span>
                          </li>
                        ) : null
                    )}
                  </ul>
                </div>
              </AbacusVariant>
            </AbacusExperiment>
          )}

        {photos && photos.length > 0 && <ReviewGallery {...review} />}

        {review.reviewText && (
          <ReviewTranslate
            reviewTranslationState={reviewTranslationState}
            translateReview={this.translateReview}
            siteLocale={this.siteLocale}
            reviewLocale={review.contentLocale}
          />
        )}

        <section className="review-main">
          {title && reviewTranslationState !== 'translated' && <h4 className="uitk-type-heading-400 all-b-padding-one">{title}</h4>}
          {title &&
            reviewTranslationState === 'translated' && (
              <h4 className="uitk-type-heading-400 all-b-padding-one translated-review-title">{translatedReviewTitle}</h4>
            )}
          {initialized && (
            <LocalizedToggle className="reviews-toggle">
              <ReviewText {...reviewTextProps} />
            </LocalizedToggle>
          )}
          {!initialized && <ReviewText {...reviewTextProps} />}
        </section>

        {stayDuration && <div className="review-stay-duration uitk-type-200 pwa-theme--grey-700 all-t-margin-two">{stayDuration}</div>}

        {reviewId && <ReviewHelpfulVote review={review} />}

        {!hideManagerResponses &&
          managementResponses &&
          managementResponses.length > 0 && (
            <section className="mgr-responses uitk-type-200 pwa-theme--grey-600 all-l-margin-six all-t-margin-three">
              {managementResponses.map(response => (
                <div id={`id-${response.id}`} key={response.id} className="mgr-response">
                  <ReviewManagerDetails l10n={l10n} managerResponse={response} />
                  <ReviewManagerResponse managerResponse={response} />
                </div>
              ))}
            </section>
          )}
      </article>
    );
  }
}
